import {CustomFieldType} from '@frontapp/workflows/custom_fields';
import _ from 'lodash';
import React from 'react';

import {Localizer} from '../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {
  defineMessage,
  Plural,
  Trans,
} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ErrorWithData} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import HubspotImgSrc from '../../../../assets/images/integrations/hubspot16.svg';
import MicrosoftDynamicsImgSrc from '../../../../assets/images/integrations/microsoft16.svg';
import OutlookImgSrc from '../../../../assets/images/integrations/outlook16.svg';
import SalesforceImgSrc from '../../../../assets/images/integrations/salesforce16.svg';
import {buildContactsManagerSyncsUrl} from '../../../../helpers/contactsManagerHelpers';
import {
  CrmSyncAccountDefaultFieldIdsEnum,
  CrmSyncErrorCodesEnum,
  CrmSyncErrorModel,
  CrmSyncModel,
  CrmSyncSourcesEnum,
} from '../../../../models/api/crmSyncModel';
import {CustomFieldModel, CustomFieldResourceTypesEnum} from '../../../../models/api/customFieldModel';
import {
  HubspotDefaultCustomFieldIdsEnum,
  hubspotHelpCenterArticle,
  HubspotOptionalDefaultFields,
  HubspotRequiredDefaultFields,
} from '../hubspot/hubspotHelpers';
import {
  SalesforceDefaultCustomFieldIdsEnum,
  salesforceHelpCenterArticle,
  SalesforceOptionalDefaultFields,
  SalesforceRequiredDefaultFields,
} from '../salesforce/salesforceHelpers';

/*
 * Constants.
 */

// number of errors we show per error category
export const maxErrorCountToShow = 10;

const mainHelpCenterLink = 'https://help.front.com';

// All these fields are not needed since we will not be using this as a real custom field.
// This is only used for the dropdown
const partialCustomFieldNotNeededFields: Omit<CustomFieldModel, 'type' | 'id' | 'name'> = {
  resourceType: CustomFieldResourceTypesEnum.ACCOUNT,
  description: '',
  enumValues: undefined,
  url: '',
  nameSlug: '',
  origin: 'front',
  createdAt: 0,
  updatedAt: 0,
};

export const defaultCrmCustomFields: ReadonlyArray<CustomFieldModel> = [
  {
    id: CrmSyncAccountDefaultFieldIdsEnum.NAME,
    type: CustomFieldType.STRING,
    name: 'name',
    ...partialCustomFieldNotNeededFields,
  },
  {
    id: CrmSyncAccountDefaultFieldIdsEnum.DOMAINS,
    type: CustomFieldType.STRING,
    name: 'email domains',
    ...partialCustomFieldNotNeededFields,
  },
  {
    id: CrmSyncAccountDefaultFieldIdsEnum.DESCRIPTION,
    type: CustomFieldType.STRING,
    name: 'description',
    ...partialCustomFieldNotNeededFields,
  },
];

/*
 * Types.
 */

export interface CrmToFrontCustomFieldMapping {
  crmFieldId: string;
  customFieldId?: number;
}

export type OnUpdateMappedFieldHandler = (
  crmFieldId: string,
  customFieldId?: number,
  newCrmFieldId?: string,
) => void;

export type CrmSyncMapping = {[T: string]: number};

export type ErrorCodeMap = {
  [x: string]: ReadonlyArray<CrmSyncErrorModel>;
};

/*
 * Helpers.
 */

export function renderDefaultCustomFieldName(customFieldId: number) {
  switch (customFieldId) {
    case CrmSyncAccountDefaultFieldIdsEnum.NAME:
      return <Trans id="accounts-crm-dialog-sync-mapping-default-field-name">Name</Trans>;
    case CrmSyncAccountDefaultFieldIdsEnum.DOMAINS:
      return <Trans id="accounts-crm-dialog-sync-mapping-default-field-domains">Email domains</Trans>;
    case CrmSyncAccountDefaultFieldIdsEnum.DESCRIPTION:
      return <Trans id="accounts-crm-dialog-sync-mapping-default-field-description">Description</Trans>;
    default: {
      // We should only get back the pre-defined fields in CrmSyncDefaultFieldMappingIdsEnum.
      reportToBugsnag(
        new ErrorWithData('[CRM Account Sync] An unknown default field id was passed in.', {customFieldId}),
      );
      return undefined;
    }
  }
}

export function renderCrmSyncSourceImage(source: CrmSyncSourcesEnum) {
  switch (source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return <img src={SalesforceImgSrc} alt="Salesforce logo" />;
    case CrmSyncSourcesEnum.HUBSPOT:
      return <img src={HubspotImgSrc} alt="HubSpot logo" />;
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return <img src={MicrosoftDynamicsImgSrc} alt="Microsoft logo" />;
    case CrmSyncSourcesEnum.OUTLOOK_DIRECTORY:
      return <img src={OutlookImgSrc} alt="Outlook logo" />;
    default:
      return null;
  }
}

export function computeCrmSyncSourceDisplayName(source: CrmSyncSourcesEnum, localize?: Localizer) {
  switch (source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return localize
        ? localize(
            defineMessage({
              id: 'accounts-crm-dialog-salesforce-display-name',
              message: 'Salesforce',
            }),
          )
        : 'Salesforce';
    case CrmSyncSourcesEnum.HUBSPOT:
      return localize
        ? localize(
            defineMessage({
              id: 'accounts-crm-dialog-hubspot-display-name',
              message: 'HubSpot',
            }),
          )
        : 'HubSpot';
    default:
      return '';
  }
}

export function computeCrmSyncMappingFromCustomFieldMapping(
  mappedFields: ReadonlyArray<CrmToFrontCustomFieldMapping>,
): {
  [T: string]: number;
} {
  return mappedFields.reduce((mapping, mappedField) => {
    if (!mappedField.customFieldId) {
      return mapping;
    }
    return {
      ...mapping,
      [mappedField.crmFieldId]: mappedField.customFieldId,
    };
  }, {});
}

export function computePlaceholderFieldIconName(customFieldId: string): IconName | undefined {
  switch (customFieldId) {
    case SalesforceDefaultCustomFieldIdsEnum.DOMAINS:
    case HubspotDefaultCustomFieldIdsEnum.DOMAINS:
      return 'mentionSmall';
    case SalesforceDefaultCustomFieldIdsEnum.NAME:
    case SalesforceDefaultCustomFieldIdsEnum.DESCRIPTION:
    case HubspotDefaultCustomFieldIdsEnum.NAME:
    case HubspotDefaultCustomFieldIdsEnum.DESCRIPTION:
    default:
      return 'contentSmall';
  }
}

export function computePlaceholderFieldName(customFieldId: string, localize: Localizer): string | undefined {
  switch (customFieldId) {
    case HubspotDefaultCustomFieldIdsEnum.NAME:
    case SalesforceDefaultCustomFieldIdsEnum.NAME:
      return localize(
        defineMessage({
          id: 'accounts-crm-name-placeholder',
          message: 'Account Name',
        }),
      );
    case HubspotDefaultCustomFieldIdsEnum.DOMAINS:
    case SalesforceDefaultCustomFieldIdsEnum.DOMAINS:
      return localize(
        defineMessage({
          id: 'accounts-crm-website-placeholder',
          message: 'Website',
        }),
      );
    case HubspotDefaultCustomFieldIdsEnum.DESCRIPTION:
    case SalesforceDefaultCustomFieldIdsEnum.DESCRIPTION:
      return localize(
        defineMessage({
          id: 'accounts-crm-description-placeholder',
          message: 'Description',
        }),
      );
    default:
      return undefined;
  }
}

export function computeCrmSyncMapping(crmSync?: CrmSyncModel): ReadonlyArray<CrmToFrontCustomFieldMapping> {
  if (!crmSync) {
    return [];
  }

  const defaultFields = computeDefaultFields(crmSync.source);

  const requiredFields = computeRequiredFields(crmSync.source);

  if (Object.values(crmSync.mapping).length === 0) {
    return defaultFields;
  }

  return _(crmSync.mapping)
    .map((customFieldId, crmFieldId) => ({crmFieldId, customFieldId}))
    .filter(
      (mapping) =>
        !requiredFields.some((requiredMapping) => requiredMapping.crmFieldId === mapping.crmFieldId),
    )
    .value();
}

function computeDefaultFields(source: CrmSyncSourcesEnum) {
  switch (source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return SalesforceOptionalDefaultFields;
    case CrmSyncSourcesEnum.HUBSPOT:
      return HubspotOptionalDefaultFields;
    default:
      return [];
  }
}

export function computeRequiredFields(source: CrmSyncSourcesEnum) {
  switch (source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return SalesforceRequiredDefaultFields;
    case CrmSyncSourcesEnum.HUBSPOT:
      return HubspotRequiredDefaultFields;
    default:
      return [];
  }
}

export function computeHelpCenterLink(source: CrmSyncSourcesEnum) {
  switch (source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return salesforceHelpCenterArticle;
    case CrmSyncSourcesEnum.HUBSPOT:
      return hubspotHelpCenterArticle;
    default:
      return mainHelpCenterLink;
  }
}

export function onViewErrorsClick(source: CrmSyncSourcesEnum, navigateToLink: (arg: string) => void) {
  navigateToLink(buildContactsManagerSyncsUrl(source, true));
}

export function buildErrorCodeMap(errors: ReadonlyArray<CrmSyncErrorModel>) {
  // Group lists of errors by their error code
  return errors.reduce<ErrorCodeMap>((result, current) => {
    if (result[current.code]) {
      return {
        ...result,
        [current.code]: [...result[current.code], current],
      };
    }
    return {
      ...result,
      [current.code]: [current],
    };
  }, {});
}

export function computeAccountSyncErrorCodeTitle(code: string, numErrors: number, source: string) {
  const numErrorsToDisplay = numErrors > maxErrorCountToShow ? maxErrorCountToShow : numErrors;
  const plusSign = numErrors > maxErrorCountToShow ? '+' : '';

  switch (code) {
    case CrmSyncErrorCodesEnum.INVALID_DOMAIN:
      return (
        <Plural
          id="crm-sync-errors-title-invalid-domain"
          value={numErrorsToDisplay}
          one="# invalid email domain was not synced"
          other={`#${plusSign} invalid email domains were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.COMMON_DOMAIN_USED:
      return (
        <Plural
          id="crm-sync-errors-title-common-domain"
          value={numErrorsToDisplay}
          one="# common email domain was not synced"
          other={`#${plusSign} common email domains were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.NAME_ALREADY_TAKEN:
      return (
        <Plural
          id="crm-sync-errors-title-same-name"
          value={numErrorsToDisplay}
          one={`# account shares the same name in ${source} and was not synced`}
          other={`#${plusSign} accounts share the same name in ${source} and were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.DOMAIN_ALREADY_TAKEN:
      return (
        <Plural
          id="crm-sync-errors-title-domain-taken"
          value={numErrorsToDisplay}
          one="# email domain is already in use and was not synced"
          other={`#${plusSign} email domains are already in use and were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.NAME_NOT_FOUND:
      return (
        <Plural
          id="crm-sync-errors-title-name-not-found"
          value={numErrorsToDisplay}
          one={`# ${source} account does not include a name and was not synced`}
          other={`#${plusSign} ${source} accounts do not include a name and were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.CRM_FIELD_NOT_FOUND:
      return (
        <Plural
          id="crm-sync-errors-title-field-not-found"
          value={numErrorsToDisplay}
          one={`# mapped ${source} field was not found and was not synced`}
          other={`#${plusSign} mapped ${source} fields were not found and were not synced`}
        />
      );
    default:
      return (
        <Plural
          id="crm-sync-errors-title-unknown"
          value={numErrorsToDisplay}
          one="# account generated unknown errors and was not synced"
          other={`#${plusSign} accounts generated unknown errors and were not synced`}
        />
      );
  }
}
